// PostHog Public Project API Key (safe to expose in frontend, same pattern as Turnstile/RevenueCat)
const POSTHOG_KEY =
  (import.meta.env.VITE_POSTHOG_KEY as string | undefined) ||
  (import.meta.env.VITE_POSTHOG_API_KEY as string | undefined) ||
  'phc_wGkL3oSkrxJtKKKF4jNBZFi4pj52SwCZWYJcewcnxaot';

const POSTHOG_HOST =
  (import.meta.env.VITE_POSTHOG_HOST as string | undefined) ||
  (import.meta.env.VITE_POSTHOG_API_HOST as string | undefined) ||
  'https://us.i.posthog.com';

let posthogInstance: any = null;
let initPromise: Promise<any> | null = null;
const eventQueue: Array<(ph: any) => void> = [];

export const initPostHog = async () => {
  if (initPromise) return initPromise;
  if (typeof window !== 'undefined' && POSTHOG_KEY && POSTHOG_KEY !== 'your-posthog-key-here') {
    initPromise = (async () => {
      try {
        const { default: posthog } = await import('posthog-js');
        posthogInstance = posthog;
        posthog.init(POSTHOG_KEY, {
          api_host: POSTHOG_HOST,
          person_profiles: 'identified_only',
          capture_pageview: true,
          capture_pageleave: true,
          autocapture: false, // FINOPS: Disable autocapture to prevent event explosion and reduce billing
          session_recording: {
            maskAllInputs: false,
            maskTextSelector: '.mask-text',
          },
          loaded: (ph) => {
            if (import.meta.env.DEV) ph.debug();
          },
        });

        // Flush any queued operations
        while (eventQueue.length > 0) {
          const action = eventQueue.shift();
          try {
            action?.(posthog);
          } catch (err) {
            console.warn("Failed to dispatch queued PostHog action", err);
          }
        }

        return posthog;
      } catch (e) {
        console.warn("Failed to load posthog-js", e);
        return null;
      }
    })();
    return initPromise;
  }
};

const withPostHog = (callback: (ph: any) => void) => {
  if (typeof window === 'undefined' || !POSTHOG_KEY || POSTHOG_KEY === 'your-posthog-key-here') return;
  if (posthogInstance) {
    callback(posthogInstance);
  } else {
    eventQueue.push(callback);
    if (!initPromise) {
      initPostHog();
    }
  }
};

export const identifyUser = (userId: string, email?: string, name?: string) => {
  withPostHog((ph) => {
    ph.identify(userId, { email, name });
  });
};

export const resetPostHog = () => {
  withPostHog((ph) => {
    ph.reset();
  });
};

export const trackEvent = (event: string, properties: Record<string, unknown> = {}) => {
  withPostHog((ph) => {
    ph.capture(event, properties);
  });
};

export const trackContentAccess = (type: 'question' | 'flashcard', resourceId: string, metadata: Record<string, unknown> = {}) => {
  trackEvent('content_accessed', {
    content_type: type,
    resource_id: resourceId,
    ...metadata,
  });
};

// Key business events
export const trackSignup = (method: 'email' | 'google' | 'discord', metadata: Record<string, unknown> = {}) =>
  trackEvent('user_signed_up', { method, ...metadata });

export const trackLogin = (method: 'email' | 'google' | 'discord') =>
  trackEvent('user_logged_in', { method });

export const trackExamCompleted = (metadata: {
  subtopic_id?: string;
  subtopic_name?: string;
  total_questions: number;
  correct_answers: number;
  accuracy: number;
  xp_gained: number;
}) => trackEvent('exam_completed', metadata);

export const trackSubscriptionStarted = (metadata: {
  plan?: string;
  price?: number;
  currency?: string;
  period?: string;
}) => trackEvent('subscription_started', metadata);

export const trackFlashcardSessionCompleted = (metadata: {
  subtopic_id?: string;
  subtopic_name?: string;
  total_cards: number;
  correct_answers: number;
  accuracy: number;
  xp_gained: number;
}) => trackEvent('flashcard_session_completed', metadata);

export const trackVideoWatched = (metadata: {
  video_id: string;
  video_title: string;
  subtopic_id?: string;
  duration?: string;
}) => trackEvent('video_watched', metadata);

export const trackOnboardingCompleted = (metadata: Record<string, unknown> = {}) =>
  trackEvent('onboarding_completed', metadata);

export const trackPaywallViewed = (metadata: {
  source?: string;
  offerings_count?: number;
} = {}) => trackEvent('paywall_viewed', metadata);

export default posthogInstance;
